import { inject, injectable } from 'inversify';

import {
  DtoBase,
  Enums,
  SharedModels,
  TenantModels,
  UIDtoFactory,
  UserModels,
} from '@project44-models/iam-gateway';

import { ServiceClientAdapter } from '../../../../shared/abstract/infrastructrue/ServiceClientAdapter';
import { TRepositoryRequestPayload } from '../../../../shared/abstract/infrastructrue/types';
import { AxiosDelegationStrategyEnum } from '../../../../shared/core/domain/enums';
import { ParametersValueObject } from '../../../../shared/core/domain/ParameterValueObject';
import { InternalAxiosConfigAdapter } from '../../../../shared/core/infrastructrue/adapters/InternalAxiosConfig.adapter';
import { UnifiedRolesAdapter } from '../../../UnifiedRoles/infrastructure/adapters/UnifiedRoles.adapter';
import {
  IUserServiceClientAdapter,
  IUserTenantSpecificUnifiedRolesQueryPayload,
  IUserUnifiedRolesUpdatePayload,
} from '../../domain/UserServiceClientAdapter';

@injectable()
export class UserServiceClientAdapter
  extends ServiceClientAdapter
  implements IUserServiceClientAdapter
{
  constructor(
    @inject(InternalAxiosConfigAdapter)
    private readonly axiosConfigAdapter: InternalAxiosConfigAdapter,

    @inject(UnifiedRolesAdapter)
    private readonly unifiedRolesAdapter: UnifiedRolesAdapter,
  ) {
    super();
  }

  public readonly getUserTenantAgnosticRoles: IUserServiceClientAdapter['getUserTenantAgnosticRoles'] =
    (payload) => {
      const { userResourceIdentifier, maybeUrlSearchParams } =
        this.composeUserWithTenantContextUnifiedRolesIdentifier(
          payload.user,
          payload.tenant,
        );

      const requestConfig: TRepositoryRequestPayload<SharedModels.UnifiedRoleCollectionViewModel> =
        {
          config: this.axiosConfigAdapter.toModifiedAxiosRequestConfig({
            url: `/admins/users/${userResourceIdentifier}/roles/tenant-agnostic`,
            method: 'GET',
            urlSearchParams: maybeUrlSearchParams,
            dtoAdapter: this.unifiedRolesAdapter.toUnifiedRoleCollectionDto,
          }),
          strategy: AxiosDelegationStrategyEnum.EXECUTABLE,
        };

      return this.handleRequest<
        SharedModels.UnifiedRoleCollectionViewModel,
        AxiosDelegationStrategyEnum.EXECUTABLE
      >(ParametersValueObject.create(requestConfig));
    };

  public readonly getUserTenantSpecificRoles: IUserServiceClientAdapter['getUserTenantSpecificRoles'] =
    async (payload: IUserTenantSpecificUnifiedRolesQueryPayload) => {
      const { userResourceIdentifier } =
        this.composeUserWithTenantContextUnifiedRolesIdentifier(
          payload.user,
          payload.tenant,
        );
      const requestConfig: TRepositoryRequestPayload<SharedModels.UnifiedRoleCollectionViewModel> =
        {
          config: this.axiosConfigAdapter.toModifiedAxiosRequestConfig({
            url: `/admins/users/${userResourceIdentifier}/roles`,
            method: 'GET',
            urlSearchParams: new URLSearchParams([
              ['tenantUuid', payload.tenant.source.getTenantUuid()],
            ]),
            dtoAdapter: this.unifiedRolesAdapter.toUnifiedRoleCollectionDto,
          }),
          strategy: AxiosDelegationStrategyEnum.EXECUTABLE,
        };

      return this.handleRequest<
        SharedModels.UnifiedRoleCollectionViewModel,
        AxiosDelegationStrategyEnum.EXECUTABLE
      >(ParametersValueObject.create(requestConfig));
    };

  public readonly updateUserTenantAgnosticRoles: IUserServiceClientAdapter['updateUserTenantAgnosticRoles'] =
    (payload: IUserUnifiedRolesUpdatePayload) => {
      const { userResourceIdentifier, maybeUrlSearchParams } =
        this.composeUserWithTenantContextUnifiedRolesIdentifier(
          payload.user,
          payload.tenant,
        );

      const requestConfig: TRepositoryRequestPayload<SharedModels.VoidResponseViewModel> =
        {
          config: this.axiosConfigAdapter.toModifiedAxiosRequestConfig({
            url: `/admins/users/${userResourceIdentifier}/roles/tenant-agnostic`,
            method: 'PUT',
            axiosBodyPayload:
              payload.unifiedRoleCollectionDto.toSerializedModel(),
            urlSearchParams: maybeUrlSearchParams,
            dtoAdapter: this.voidResponseDtoAdapter,
          }),
          strategy: AxiosDelegationStrategyEnum.EXECUTABLE,
        };

      return this.handleRequest<
        SharedModels.VoidResponseViewModel,
        AxiosDelegationStrategyEnum.EXECUTABLE
      >(ParametersValueObject.create(requestConfig));
    };

  public readonly updateUserTenantSpecificRoles: IUserServiceClientAdapter['updateUserTenantSpecificRoles'] =
    async (payload: IUserUnifiedRolesUpdatePayload) => {
      const { userResourceIdentifier } =
        this.composeUserWithTenantContextUnifiedRolesIdentifier(
          payload.user,
          payload.tenant,
        );

      const requestConfig: TRepositoryRequestPayload<SharedModels.VoidResponseViewModel> =
        {
          config: this.axiosConfigAdapter.toModifiedAxiosRequestConfig({
            url: `/admins/users/${userResourceIdentifier}/roles`,
            method: 'PUT',
            axiosBodyPayload:
              payload.unifiedRoleCollectionDto.toSerializedModel(),
            urlSearchParams: new URLSearchParams([
              ['tenantUuid', payload.tenant.source.getTenantUuid()],
            ]),
            dtoAdapter: this.voidResponseDtoAdapter,
          }),
          strategy: AxiosDelegationStrategyEnum.EXECUTABLE,
        };

      return this.handleRequest<
        SharedModels.VoidResponseViewModel,
        AxiosDelegationStrategyEnum.EXECUTABLE
      >(ParametersValueObject.create(requestConfig));
    };

  public readonly createUser: IUserServiceClientAdapter['createUser'] = async ({
    userDetails,
  }) => {
    const requestConfig: TRepositoryRequestPayload<UserModels.UserViewModel> = {
      config: this.axiosConfigAdapter.toModifiedAxiosRequestConfig({
        url: `/admins/users`,
        method: 'POST',
        axiosBodyPayload: userDetails.toJSON(),
        dtoAdapter: (data) => {
          return UIDtoFactory.create(Enums.UiDtoFactoryEnum.USER, data);
        },
      }),
      strategy: AxiosDelegationStrategyEnum.EXECUTABLE,
    };

    const { result } = await this.handleRequest<
      UserModels.UserViewModel,
      AxiosDelegationStrategyEnum.EXECUTABLE
    >(ParametersValueObject.create(requestConfig));

    return {
      user: result,
    };
  };

  private voidResponseDtoAdapter(
    data: SharedModels.VoidResponseViewModel,
  ): DtoBase<SharedModels.VoidResponseViewModel> {
    return UIDtoFactory.create(Enums.UiDtoFactoryEnum.VOID_RESPONSE, data);
  }

  private composeUserWithTenantContextUnifiedRolesIdentifier(
    user: DtoBase<UserModels.UserViewModel>,
    tenant: DtoBase<TenantModels.TenantViewModel>,
  ): {
    maybeUrlSearchParams: URLSearchParams | undefined;
    userResourceIdentifier: string;
  } {
    const resourceIdentifierModel = this.unifiedRolesAdapter
      .toUserWithTenantContextUnifiedRolesIdentifier(user, tenant)
      .toSerializedModel();

    return {
      maybeUrlSearchParams: resourceIdentifierModel.queryParams,
      userResourceIdentifier: resourceIdentifierModel.resourceIdentifier,
    };
  }

  public readonly getUserById: IUserServiceClientAdapter['getUserById'] =
    async ({ userId, tenant, includeDeleted }) => {
      const requestConfig: TRepositoryRequestPayload<UserModels.UserViewModel> =
        {
          config: this.axiosConfigAdapter.toModifiedAxiosRequestConfig({
            url: `/admins/users/${userId}`,
            method: 'GET',
            urlSearchParams: new URLSearchParams([
              ['tenantUuid', tenant.source.getTenantUuid()],
              ...(includeDeleted
                ? [['includeDeleted', includeDeleted.toString()]]
                : []),
            ]),
            dtoAdapter: (data) => {
              return UIDtoFactory.create(Enums.UiDtoFactoryEnum.USER, data);
            },
          }),
          strategy: AxiosDelegationStrategyEnum.EXECUTABLE,
        };

      const { result } = await this.handleRequest<
        UserModels.UserViewModel,
        AxiosDelegationStrategyEnum.EXECUTABLE
      >(ParametersValueObject.create(requestConfig));

      return {
        user: result,
      };
    };
}
